import { Injectable, signal } from '@angular/core';

import {
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  GoogleAuthProvider,
  multiFactor,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  TotpMultiFactorGenerator,
  TotpSecret,
} from 'firebase/auth';

import { AlertService } from './alert.service';
import { CatchErrorService } from './catch.service';
import { AuthUser } from './firebase.service';

export type AppAuthProvider = 'apple' | 'google' | 'email';

export interface SignInResult {
  readonly success: boolean;
  readonly mfaRequired: boolean;
  readonly resolver?: MultiFactorResolver;
}

/**
 * Runs a sensitive auth action and, if Firebase reports the session isn't
 * fresh enough (`requires-recent-login`), surfaces a reauth prompt and
 * retries the same action once the user re-authenticates. One instance per
 * in-flight sensitive action - components own the instance and bind their
 * `<app-reauth-dialog>` to `visible`/`onVisibleChange`/`confirmed`.
 */
export class ReauthGate {
  readonly visible = signal(false);
  private pending: (() => Promise<boolean>) | null = null;

  /**
   * Runs `action`. `action` performs the sensitive operation itself and
   * returns `true` if it failed specifically because of
   * `requires-recent-login` - any other outcome (success or a different
   * error) isn't this gate's concern and should be handled by the caller
   * inside `action` before it returns `false`.
   */
  async run(action: () => Promise<boolean>): Promise<void> {
    const needsReauth = await action();
    if (needsReauth) {
      this.pending = action;
      this.visible.set(true);
    }
  }

  /**
   * Bind to the reauth dialog's `(visibleChange)`. Returns true if this
   * closed a still-pending retry - i.e. a genuine cancel (X/Cancel button)
   * rather than the dialog closing itself after `confirmed()` already ran.
   */
  onVisibleChange(visible: boolean): boolean {
    this.visible.set(visible);
    if (visible) return false;
    const wasPending = this.pending !== null;
    this.pending = null;
    return wasPending;
  }

  /**
   * Drops a stored retry without running it - for callers that need to resume
   * the flow themselves rather than blindly replaying the original action
   * (e.g. a TOTP code that will have expired during reauth).
   */
  discardPending(): void {
    this.pending = null;
    this.visible.set(false);
  }

  /** Bind to the reauth dialog's `(confirmed)` - retries the pending action. */
  async confirmed(): Promise<void> {
    const action = this.pending;
    this.pending = null;
    this.visible.set(false);
    if (action) await this.run(action);
  }
}

/**
 * TOTP multi-factor auth helpers. Firebase is the source of truth for
 * enrollment state; the backend `mfaEnabled` flag is only a mirror.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private readonly as: AlertService,
    private readonly cx: CatchErrorService,
  ) {}

  /**
   * Returns a resolver when `error` is `auth/multi-factor-auth-required`,
   * otherwise null. The login widget calls this from its own sign-in catch
   * block to switch to the code-entry view.
   */
  getMfaResolver(error: unknown): MultiFactorResolver | null {
    if (!this.isMfaRequiredError(error)) return null;
    return getMultiFactorResolver(getAuth(), error);
  }

  /**
   * Completes an in-progress MFA sign-in by asserting a TOTP code against
   * the resolver captured when Firebase reported
   * `auth/multi-factor-auth-required`.
   * @param resolver MultiFactorResolver captured from a prior sign-in
   * @param code 6-digit TOTP code entered by the user
   */
  async resolveMfa(
    resolver: MultiFactorResolver,
    code: string,
  ): Promise<boolean> {
    try {
      // hints[0]: only TOTP is offered as a factor today, so there's only
      // ever one hint. Revisit if a second factor type is added.
      const hint = resolver.hints[0];
      if (!hint) {
        throw new Error('No MFA factors enrolled on resolver');
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        hint.uid,
        code,
      );
      const result = await resolver.resolveSignIn(assertion);
      return !!result;
    } catch (ex) {
      // Wrong code is an expected, common case - callers already show their
      // own inline error for it, so don't also pop a raw Firebase alert.
      const errorCode = (ex as { code?: string })?.code ?? '';
      if (errorCode !== 'auth/invalid-verification-code') {
        await this.handleException(ex);
      }
      return false;
    }
  }

  /** True if the user has a TOTP factor enrolled - source of truth is Firebase, not the backend `mfaEnabled` flag */
  isMfaEnrolled(user: AuthUser): boolean {
    return multiFactor(user).enrolledFactors.length > 0;
  }

  /**
   * Opens an MFA session and generates a shared TOTP secret for QR/manual entry.
   * Returns `requires-recent-login` if the user's session isn't fresh enough
   * to start enrollment - the caller should reauthenticate and retry.
   */
  async startMfaEnrollment(user: AuthUser): Promise<
    | { status: 'success'; secret: TotpSecret }
    | { status: 'requires-recent-login' | 'error' }
  > {
    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      return { status: 'success', secret };
    } catch (ex) {
      if (this.isRequiresRecentLoginError(ex)) {
        return { status: 'requires-recent-login' };
      }
      await this.handleException(ex);
      return { status: 'error' };
    }
  }

  /**
   * Verifies a TOTP code against the pending secret and completes enrollment.
   * @param secret Secret returned by `startMfaEnrollment()`
   * @param code 6-digit TOTP code entered by the user
   */
  async completeMfaEnrollment(
    user: AuthUser,
    secret: TotpSecret,
    code: string,
  ): Promise<'success' | 'invalid-code' | 'requires-recent-login' | 'error'> {
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        code,
      );
      await multiFactor(user).enroll(assertion, 'Authenticator App');
      return 'success';
    } catch (ex) {
      const errorCode = (ex as { code?: string })?.code ?? '';
      if (errorCode === 'auth/invalid-verification-code') return 'invalid-code';
      if (this.isRequiresRecentLoginError(ex)) return 'requires-recent-login';
      await this.handleException(ex);
      return 'error';
    }
  }

  /**
   * Returns the user's enrolled TOTP factor, or null if none is enrolled.
   * Takes index 0 since only one TOTP factor can be enrolled today.
   */
  getEnrolledMfaFactor(user: AuthUser): MultiFactorInfo | null {
    return multiFactor(user).enrolledFactors[0] ?? null;
  }

  /** Removes an enrolled TOTP factor. */
  async unenrollMfa(
    user: AuthUser,
    factor: MultiFactorInfo,
  ): Promise<'success' | 'requires-recent-login' | 'error'> {
    try {
      await multiFactor(user).unenroll(factor);
      return 'success';
    } catch (ex) {
      if (this.isRequiresRecentLoginError(ex)) return 'requires-recent-login';
      await this.handleException(ex);
      return 'error';
    }
  }

  /**
   * Determines which provider to reauthenticate with, based on the user's
   * linked provider. Only email/google/apple sign-in is offered today.
   */
  getPrimaryProvider(user: AuthUser): AppAuthProvider | null {
    switch (user.providerData[0]?.providerId) {
      case 'password':
        return 'email';
      case 'google.com':
        return 'google';
      case 'apple.com':
        return 'apple';
      default:
        return null;
    }
  }

  /**
   * Reauthenticates the current user in place, without changing the signed-in
   * user, to satisfy Firebase's `auth/requires-recent-login` requirement for
   * sensitive actions like MFA enrollment/disablement. If the account already
   * has a TOTP factor enrolled (the disable-MFA case, necessarily), Firebase
   * requires that second factor here too - so this can come back needing an
   * MFA resolution, resolved via `resolveMfa()`.
   * @param password Only used for `"email"` provider
   */
  async reauthenticate(
    user: AuthUser,
    provider: AppAuthProvider,
    password?: string,
  ): Promise<SignInResult> {
    try {
      switch (provider) {
        case 'email':
          if (!user.email) {
            throw new Error('User has no email to reauthenticate with.');
          }
          const credential = EmailAuthProvider.credential(
            user.email,
            password ?? '',
          );
          await reauthenticateWithCredential(user, credential);
          break;
        case 'apple':
          await reauthenticateWithPopup(user, new OAuthProvider('apple.com'));
          break;
        case 'google':
          await reauthenticateWithPopup(user, new GoogleAuthProvider());
          break;
      }
      return { success: true, mfaRequired: false };
    } catch (ex) {
      if (this.isMfaRequiredError(ex)) {
        const resolver = getMultiFactorResolver(getAuth(), ex);
        return { success: false, mfaRequired: true, resolver };
      }
      // A wrong password is expected and common - the reauth dialog shows its
      // own inline error for it, so don't also pop a raw Firebase alert.
      if (!this.isInvalidCredentialError(ex)) {
        await this.handleException(ex);
      }
      return { success: false, mfaRequired: false };
    }
  }

  /**
   * Takes the exception and processes into the correct message to display.
   * Only reached for reauth/enroll errors - the login widget has its own
   * fuller handler for the sign-in path.
   */
  private async handleException(ex: any): Promise<void> {
    if (typeof ex === 'string') {
      this.cx.handle(new Error(ex), true);
      return;
    }
    if (!ex) {
      this.cx.handle(ex, false, 'Unknown error during authentication');
      return;
    }

    switch (ex?.code ?? '') {
      case 'auth/wrong-password':
        await this.as.showAlert({
          title: 'Error',
          message: 'Failed to sign in, check your password and email.',
        });
        break;
      case 'auth/network-request-failed':
        await this.as.showAlert({
          title: 'Connection Error',
          message: 'Failed to connect to server, please try again.',
        });
        break;
      case 'auth/popup-blocked':
        await this.as.showAlert({
          title: 'Error',
          message:
            'Pop ups blocked by browser! Enable popups, refresh this page and try again.',
        });
        break;
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
      case 'auth/user-cancelled':
        return;
      case 'auth/multi-factor-auth-required':
        // Intercepted by callers via getMfaResolver() before reaching here.
        return;
      case 'auth/requires-recent-login':
        // Intercepted by callers that trigger the reauth flow.
        return;
      default:
        this.cx.handle(ex, true);
    }
  }

  /** Returns true if the error code is `"auth/multi-factor-auth-required"` */
  private isMfaRequiredError(ex: unknown): ex is MultiFactorError {
    return (
      !!ex &&
      typeof ex === 'object' &&
      (ex as { code?: string }).code === 'auth/multi-factor-auth-required'
    );
  }

  /** Returns true for the "bad password / credential" error codes Firebase has used across versions */
  private isInvalidCredentialError(ex: unknown): boolean {
    const code = (ex as { code?: string })?.code ?? '';
    return (
      code === 'auth/wrong-password' ||
      code === 'auth/invalid-credential' ||
      code === 'auth/invalid-login-credentials'
    );
  }

  /** Returns true if the error code is `"auth/requires-recent-login"` */
  private isRequiresRecentLoginError(ex: unknown): boolean {
    return (
      !!ex &&
      typeof ex === 'object' &&
      (ex as { code?: string }).code === 'auth/requires-recent-login'
    );
  }
}
